import {
  Button,
  Color,
  Spin,
  Statistic,
  Tooltip,
  Flex,
} from '@bb/elements-web';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import AuthContext from 'contexts/AuthContext';
import {
  SupraSubscriptionProductBillingModel,
  SupraSystemSubscriptionStatus,
} from '@bb/domain/dist/types';
import { useAnsweringServiceDetails } from 'hooks';
import { getOrgBillingModel } from 'api/SupraController';
import { ViewingLevel } from '../..';
import {
  CanManageBillingProfileType,
  useSubscriptionsPageContext,
} from '../../SubscriptionsPageContext';
import { CardErrorTag } from '../CardErrorTag';

/**
 * Formats a date string to "MMM D, YYYY" format (e.g., "Mar 20, 2025")
 */
export const formatBillingDate = (date?: string | Date): string => {
  if (!date) return '-';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(dateObj.getTime())) {
    return typeof date === 'string' ? date : '';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(dateObj);
};

interface BillingCardProps {
  /** Indicates (via `response`) whether the user has permission to manage some aspect of the billing profile.
        If the user does not have permission, the `message` field will contain the reason why. */
  canManageBilling: CanManageBillingProfileType;
  /** Does the current user or brokerage have an overdue balance on any of their subscriptions */
  isSubscriptionOverdue: boolean;
  /** Whether these are the subscriptions for an indivdual user or for a brokerage paying for all its users */
  viewingLevel: ViewingLevel;
  /** Click handler for when Pay Now is clicked */
  onPayNowClick: () => void;
}

/**
 * Displays billing information (renewal date or outstanding balance) for the Payment card.
 */
const BillingCard = ({
  canManageBilling,
  viewingLevel,
  isSubscriptionOverdue,
  onPayNowClick,
}: BillingCardProps) => {
  const { auth, loadingSubscriptionsBillingDetails } = useContext(AuthContext);
  const { overdueBalance, suspensionDate, nextPaymentDueDate } =
    useSubscriptionsPageContext();

  const [hasSubscriptions, setHasSubscriptions] = useState(false);
  const [fetchingHasSubscriptions, setFetchingHasSubscriptions] =
    useState(false);

  const manageBillingMessage =
    'message' in canManageBilling ? canManageBilling.message : undefined;

  /** Set whether this user has an active Supra One lockbox subscription. */
  const hasActiveSupraSystemSubscription = useCallback(async () => {
    // Brokerages cannot pay for a Supra One lockbox subscription.
    if (viewingLevel === 'org') return;

    // Get the billing model (i.e. who is responsible for paying for Supra One subscriptions in this brokerage)
    const billingModel = await getOrgBillingModel({ orgId: auth.org.id });
    const userPaysForSupraOne =
      billingModel === SupraSubscriptionProductBillingModel.Keyholder;

    // Check the user-level subscription
    if (
      viewingLevel === 'user' &&
      !!auth.supraSubscription &&
      // Only active subscriptions
      auth.supraSubscription.status !==
        SupraSystemSubscriptionStatus.unsubscribed &&
      // Only subscriptions the user pays themself
      userPaysForSupraOne
    ) {
      setHasSubscriptions(true);
    }
  }, [auth.org.id, auth.supraSubscription, viewingLevel]);

  // Set up Answering Service data
  const {
    answeringServicePayer,
    answeringServiceType,
    hasActiveAnsweringService,
  } = useAnsweringServiceDetails({
    viewingLevel,
    userId: auth.id,
  });
  const isPayingForOwnSub =
    (answeringServicePayer === 'brokerage' && viewingLevel === 'org') ||
    (answeringServicePayer === 'user' && viewingLevel === 'user');
  /** Whether this user/brokerage currently pays for an active Answering Service subscription. */
  const hasActiveAnsweringServiceSubscription = useCallback(async () => {
    if (
      hasActiveAnsweringService &&
      isPayingForOwnSub &&
      answeringServiceType !== 'Legacy'
    ) {
      setHasSubscriptions(true);
    }
  }, [hasActiveAnsweringService, isPayingForOwnSub, answeringServiceType]);

  // Run when the page loads to check if there are any active subscriptions.
  useEffect(() => {
    let isMounted = true;

    const fetchSubscriptions = async () => {
      try {
        setFetchingHasSubscriptions(true);

        await Promise.all([
          hasActiveSupraSystemSubscription(),
          hasActiveAnsweringServiceSubscription(),
        ]);
      } finally {
        if (isMounted) setFetchingHasSubscriptions(false);
      }
    };

    fetchSubscriptions();

    return () => {
      isMounted = false;
    };
  }, [hasActiveSupraSystemSubscription, hasActiveAnsweringServiceSubscription]);

  // Determine which of the four outputs we want to return:

  // Output 1: DB fetch is still loading
  if (loadingSubscriptionsBillingDetails || fetchingHasSubscriptions) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{ width: '100%', padding: '8px 0' }}
      >
        <Spin dataCy="subscription-payment-spinner" />
      </Flex>
    );
  }

  // Output 2: Customer has no subscriptions they pay for
  if (!hasSubscriptions) {
    return <BillingText>You are not paying for any subscriptions.</BillingText>;
  }

  // Output 3: Customer has overdue payment
  if (isSubscriptionOverdue) {
    return (
      <StyledBody>
        <StyledColumn>
          <StyledSection>
            <StyledBalanceGroup>
              <StyledBalanceText value={overdueBalance} />
              <CardErrorTag>Past Due</CardErrorTag>
            </StyledBalanceGroup>
            <Tooltip
              title={canManageBilling.response ? '' : manageBillingMessage}
            >
              <Button
                dataCy={'pay-now-button'}
                minWidth="auto"
                size="small"
                disabled={!canManageBilling.response}
                onClick={onPayNowClick}
                style={{ padding: '6px 16px' }}
              >
                Pay Now
              </Button>
            </Tooltip>
          </StyledSection>
          <BillingText>
            You must pay your balance by {formatBillingDate(suspensionDate)} or
            your paid subscriptions will be suspended.
          </BillingText>
        </StyledColumn>
      </StyledBody>
    );
  }

  // Output 4: Customer has subscriptions that are all in good standing
  return (
    <BillingText>
      Your subscriptions will renew on {formatBillingDate(nextPaymentDueDate)}.
    </BillingText>
  );
};

const BillingText = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: ${Color.PRIMARY_TEXT};
`;

const StyledBody = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  grid-row-gap: 25px;
  grid-column-gap: 25px;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const StyledBalanceGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StyledBalanceText = styled(Statistic)`
  margin-right: 0;
  padding-bottom: 0;
  .ant5-statistic-content {
    color: ${Color.DODORIA};
    font-weight: bold;
    font-size: 32px;
  }
`;

export default BillingCard;
